import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { 
  ArrowLeft, BookOpen, Shield, Users, Key, Banknote, 
  ChevronRight, Clock, Search, Mail
} from "lucide-react";

const TenUResLogo = ({ size = "text-4xl" }) => (
  <span className={`tenures-logo ${size}`}>
    Ten<span className="tenures-ur font-extrabold">UR</span>es
  </span>
);

const categories = [
  { id: "succession", name: "Succession Secrets", icon: Shield, color: "emerald", description: "How to protect your home for the next generation." },
  { id: "exchange", name: "The Swap Strategy", icon: Users, color: "blue", description: "Why 'Liveability' beats 'Square Footage' in a mutual exchange." },
  { id: "rtb", name: "Unlock Your Home", icon: Key, color: "purple", description: "Navigating the Right to Buy without the stress." },
  { id: "officer", name: "Officer Tips", icon: Banknote, color: "amber", description: "How to communicate effectively with your Local Authority." }
];

const articles = [
  {
    id: 1,
    title: "The 'Cost Floor' Explained: Why Your Discount Might Be Smaller Than You Think",
    snippet: "Most residents assume they get the full statutory discount, but the 'Cost Floor' rule can change everything. Learn how to ask your Officer for the right figures and what documentation you need to challenge an incorrect calculation.",
    category: "rtb",
    readTime: "7 min read",
    featured: true
  },
  {
    id: 2,
    title: "5 Documents You Need Before Your Succession Meeting",
    snippet: "Walking into a succession meeting unprepared is the #1 reason applications get delayed. Here's your checklist of essential documents that prove continuous residence.",
    category: "succession",
    readTime: "5 min read",
    featured: true
  },
  {
    id: 3,
    title: "The Liveability Match: Why Bedroom Count Isn't Everything",
    snippet: "90% of successful exchanges happen when residents match on lifestyle factors, not just space. Discover the hidden criteria that make swaps work.",
    category: "exchange",
    readTime: "6 min read",
    featured: true
  },
  {
    id: 4,
    title: "How to Write an Effective Letter to Your Housing Officer",
    snippet: "The right tone and terminology can make the difference between a quick response and weeks of waiting. Use our template to get heard.",
    category: "officer",
    readTime: "4 min read",
    featured: false
  },
  {
    id: 5,
    title: "Succession Rights After a Second Tenancy: What You Need to Know",
    snippet: "If your tenancy has already been succeeded once, your options change dramatically. But that doesn't mean you're out of luck.",
    category: "succession",
    readTime: "8 min read",
    featured: false
  },
  {
    id: 6,
    title: "Understanding Preserved Right to Buy: A Guide for Stock Transfer Residents",
    snippet: "If your home was transferred from the Council to a Housing Association, your rights might be different. Here's how to check your status.",
    category: "rtb",
    readTime: "6 min read",
    featured: false
  }
];

const CategoryBadge = ({ category }) => {
  const cat = categories.find(c => c.id === category);
  const colorClasses = {
    emerald: "bg-emerald-100 text-emerald-700",
    blue: "bg-blue-100 text-blue-700",
    purple: "bg-purple-100 text-purple-700",
    amber: "bg-amber-100 text-amber-700"
  };
  
  return (
    <span className={`text-xs font-medium px-2 py-1 rounded ${colorClasses[cat?.color] || 'bg-gray-100 text-gray-700'}`}>
      {cat?.name || category}
    </span>
  );
};

const ArticleCard = ({ article, featured = false }) => (
  <Card className={`bg-white border-gray-200 hover:border-[#05D38A] hover:shadow-lg transition-all cursor-pointer group ${featured ? 'md:col-span-1' : ''}`}>
    <CardContent className="p-6">
      <div className="flex items-center gap-2 mb-3">
        <CategoryBadge category={article.category} />
        <span className="text-xs text-gray-400 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {article.readTime}
        </span>
      </div>
      <h3 className="font-bold text-[#111827] mb-2 group-hover:text-[#05D38A] transition-colors">
        {article.title}
      </h3>
      <p className="text-gray-600 text-sm leading-relaxed mb-4">
        {article.snippet}
      </p>
      <div className="flex items-center gap-1 text-[#05D38A] text-sm font-medium">
        Read Article <ChevronRight className="w-4 h-4" />
      </div>
    </CardContent>
  </Card>
);

export default function Library() {
  const navigate = useNavigate();

  const featuredArticles = articles.filter(a => a.featured);
  const moreArticles = articles.filter(a => !a.featured);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 nav-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <Link to="/">
            <TenUResLogo size="text-2xl" />
          </Link>
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              className="text-[#111827] hover:text-[#05D38A] font-medium"
              onClick={() => navigate('/login')}
            >
              Sign In
            </Button>
            <Button 
              className="bg-[#05D38A] hover:bg-[#04B577] text-[#111827] font-semibold"
              onClick={() => navigate('/register')}
            >
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link 
              to="/" 
              className="inline-flex items-center text-gray-500 hover:text-[#05D38A] mb-8 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Link>
            
            <div className="flex items-center gap-3 mb-4">
              <BookOpen className="w-8 h-8 text-[#05D38A]" />
              <span className="text-sm font-semibold text-[#05D38A] uppercase tracking-wide">TenURes Library</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-bold text-[#111827] mb-4">
              Master Your Housing Rights.
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Expert guides written in plain English by housing professionals with decades of frontline experience.
            </p>

            {/* Search Bar */}
            <div className="relative max-w-xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search guides..."
                className="pl-12 h-12 bg-white border-gray-300 focus:border-[#05D38A] text-[#111827]"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
          >
            {categories.map((cat) => {
              const Icon = cat.icon;
              const colorClasses = {
                emerald: "bg-emerald-50 border-emerald-200 hover:border-emerald-400",
                blue: "bg-blue-50 border-blue-200 hover:border-blue-400",
                purple: "bg-purple-50 border-purple-200 hover:border-purple-400",
                amber: "bg-amber-50 border-amber-200 hover:border-amber-400"
              };
              const iconClasses = {
                emerald: "text-emerald-600",
                blue: "text-blue-600",
                purple: "text-purple-600",
                amber: "text-amber-600"
              };
              
              return (
                <Card key={cat.id} className={`cursor-pointer transition-all ${colorClasses[cat.color]}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Icon className={`w-5 h-5 ${iconClasses[cat.color]}`} />
                      <div>
                        <h3 className="font-bold text-[#111827] text-sm">{cat.name}</h3>
                        <p className="text-gray-500 text-xs">{cat.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Featured Articles */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h2 className="text-2xl font-bold text-[#111827] mb-6">Featured Guides</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featuredArticles.map((article) => (
                <ArticleCard key={article.id} article={article} featured />
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* More Articles */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h2 className="text-2xl font-bold text-[#111827] mb-6">More Reading</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {moreArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#05D38A]/5">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <Mail className="w-12 h-12 text-[#05D38A] mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#111827] mb-2">
              Stay Informed
            </h2>
            <p className="text-gray-600 mb-6">
              Join <strong>1,000+ residents</strong> getting monthly housing tips and legal updates.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                className="bg-white border-gray-300 focus:border-[#05D38A] h-12"
              />
              <Button className="bg-[#05D38A] hover:bg-[#04B577] text-[#111827] font-semibold h-12 px-6 whitespace-nowrap">
                Subscribe
              </Button>
            </div>
            <p className="text-gray-400 text-xs mt-3">
              No spam. Unsubscribe anytime.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="font-bold text-[#111827] mb-4">Platform</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><Link to="/succession" className="hover:text-[#05D38A]">Succession Triage</Link></li>
                <li><Link to="/exchange" className="hover:text-[#05D38A]">Mutual Exchange</Link></li>
                <li><Link to="/downsizing" className="hover:text-[#05D38A]">Downsizing</Link></li>
                <li><Link to="/unlock" className="hover:text-[#05D38A]">Right to Buy</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-[#111827] mb-4">Resources</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><Link to="/library" className="hover:text-[#05D38A]">Knowledge Hub</Link></li>
                <li><Link to="/about" className="hover:text-[#05D38A]">About Us</Link></li>
                <li><Link to="/providers" className="hover:text-[#05D38A]">For Providers</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-[#111827] mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><Link to="/privacy" className="hover:text-[#05D38A]">Privacy & Security</Link></li>
                <li><Link to="/terms" className="hover:text-[#05D38A]">Terms of Service</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-[#111827] mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><Link to="/contact" className="hover:text-[#05D38A]">Get in Touch</Link></li>
                <li><span>hello@tenures.app</span></li>
              </ul>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-8 border-t border-gray-200">
            <TenUResLogo size="text-xl" />
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} TenURes. Advisory tool for social housing residents.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
